import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  formatarDataApenas,
  formatarDataHoraBR,
  formatarDataHoraCurta,
  dataConclusaoItem,
  formatarCnpjCpf,
  extrairListaValidacoes,
  resolverIdDoc,
  resolverCnpjDoc,
  resolverNomeEmpresaDoc,
  resolverNomeEstudanteDoc,
  tipoDoc,
  rotaDoc,
  endpointAtribuir,
  endpointLiberar,
  deduplicar,
  ordenarPorMaisRecente,
  recortarPagina,
  ajustarPagina,
  filtrarLista,
  contarPorTipo,
  obterCacheFilaNovosInicial,
  obterCacheFilaMinhasInicial,
  verificarPermissaoValidacoes as verificarPermissao,
} from "./utils.js";
import { API_BASE_URL } from "../../../js/api.js";
import { toDateInputValue } from "../../../js/dateHelpers.js";
import { invalidarCacheListaAcompanhamento } from "../Acompanhamento/utils/session.js";
import {
  FaHandPointer,
  FaHandshake,
  FaEye,
  FaClipboardCheck,
  FaArrowLeft,
  FaSearch,
  FaTimes,
  FaInfoCircle,
  FaExternalLinkAlt,
  FaSearchPlus,
  FaSyncAlt,
} from "react-icons/fa";
import ToastContainer from "../../components/Toast/ToastContainer.js";
import useToast from "../../hooks/useToast.js";
import useConfirm from "../../hooks/useConfirm.js";
import "./style.css";

const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];

const Validacoes = () => {
  const navigate = useNavigate();
  const {
    toasts,
    error: toastError,
    success: toastSuccess,
    removeToast,
  } = useToast();
  const { confirm, confirmDialog } = useConfirm();

  const [abaAtiva, setAbaAtiva] = useState("novos");
  const [novos, setNovos] = useState(() => obterCacheFilaNovosInicial(API_BASE_URL));
  const [minhas, setMinhas] = useState(() => obterCacheFilaMinhasInicial());
  const [loadingNovos, setLoadingNovos] = useState(false);
  const [loadingMinhas, setLoadingMinhas] = useState(false);
  const [atualizandoManual, setAtualizandoManual] = useState(false);
  const [processandoChave, setProcessandoChave] = useState(null);

  // Busca e filtros rápidos
  const [termoBusca, setTermoBusca] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("todos");

  const [tamanhoPagina, setTamanhoPagina] = useState(10);
  const [paginaNovos, setPaginaNovos] = useState(1);
  const [paginaMinhas, setPaginaMinhas] = useState(1);

  const novosRef = useRef(novos);
  const minhasRef = useRef(minhas);
  useEffect(() => {
    novosRef.current = novos;
  }, [novos]);
  useEffect(() => {
    minhasRef.current = minhas;
  }, [minhas]);

  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const abrirDocumento = useCallback(
    (itemOuUrl, origemHint) => {
      const origem =
        origemHint ||
        (typeof itemOuUrl === "object" ? itemOuUrl?._origem : "");
      try {
        if (origem === "TCE" || origem === "tce") {
          sessionStorage.setItem("modulo_ativo", "tce");
        } else if (origem === "TCA-Empregador" || origem === "tca-empregador") {
          sessionStorage.setItem("modulo_ativo", "tca-empregador");
        } else if (origem === "TCA" || origem === "tca") {
          sessionStorage.setItem("modulo_ativo", "tca");
        }
      } catch (_) {}

      const destino =
        typeof itemOuUrl === "string" ? itemOuUrl : rotaDoc(itemOuUrl);
      navigate(destino);
    },
    [navigate],
  );

  // Diagnóstico direto de documento por ID (em paralelo nos 3 programas)
  const [diagnostico, setDiagnostico] = useState(null);
  const [loadingDiagnostico, setLoadingDiagnostico] = useState(false);

  const consultarDocumentoPorId = async (idNum) => {
    if (!idNum) return;
    setLoadingDiagnostico(true);
    setDiagnostico(null);
    try {
      const endpoints = [
        {
          rota: `/tca-empregador/${idNum}`,
          tipo: "TCA Empregador",
          origem: "TCA-Empregador",
          urlDoc: `/tca-empregador?id=${idNum}`,
        },
        {
          rota: `/tca/${idNum}`,
          tipo: "TCA",
          origem: "TCA",
          urlDoc: `/tca?id=${idNum}`,
        },
        {
          rota: `/tce/${idNum}`,
          tipo: "TCE",
          origem: "TCE",
          urlDoc: `/tce?id=${idNum}`,
        },
      ];

      const resultados = await Promise.allSettled(
        endpoints.map(async (ep) => {
          const res = await axios.get(`${API_BASE_URL}${ep.rota}`, {
            headers: getAuthHeader(),
            timeout: 8000,
          });
          const doc = res?.data?.data || res?.data;
          if (doc && (doc.id || doc.tce_id || doc.tca_id || doc.status)) {
            return {
              id: resolverIdDoc(doc) || idNum,
              tipo: ep.tipo,
              origem: ep.origem,
              status: doc.status || doc.status_label || "PENDENTE",
              validadorId: doc.validador_id,
              empresaNome: resolverNomeEmpresaDoc(doc),
              estudanteNome: resolverNomeEstudanteDoc(doc),
              cnpjVal: formatarCnpjCpf(resolverCnpjDoc(doc)),
              urlDoc: ep.urlDoc,
            };
          }
          return null;
        }),
      );

      const encontrados = resultados
        .filter((r) => r.status === "fulfilled" && r.value)
        .map((r) => r.value);

      if (encontrados.length > 0) {
        const principal =
          (filtroTipo !== "todos" &&
            encontrados.find((e) => e.origem === filtroTipo)) ||
          encontrados[0];
        setDiagnostico({
          encontrado: true,
          ...principal,
          encontrados,
        });
      } else {
        setDiagnostico({
          encontrado: false,
          id: idNum,
        });
      }
    } catch (err) {
      setDiagnostico({
        encontrado: false,
        id: idNum,
        erro: err.message,
      });
    } finally {
      setLoadingDiagnostico(false);
    }
  };

  const safeGetFila = useCallback(
    async (path, label, salvarNoCacheSidebar = false) => {
      try {
        const url = `${API_BASE_URL}${path}`;
        const r = await axios.get(url, {
          headers: getAuthHeader(),
          timeout: 10000,
        });
        const lista = extrairListaValidacoes(r?.data);
        if (
          salvarNoCacheSidebar &&
          typeof window !== "undefined" &&
          !(typeof process !== "undefined" && process.env?.NODE_ENV === "test")
        ) {
          window.__cieeValidacoesNovosCache =
            window.__cieeValidacoesNovosCache || {};
          window.__cieeValidacoesNovosCache[url] = {
            ts: Date.now(),
            data: lista,
          };
        }
        const data = lista.map((it) => ({
          ...it,
          id: resolverIdDoc(it),
          _origem: label,
        }));
        return { ok: true, label, data };
      } catch (err) {
        const status = err?.response?.status;
        const data = err?.response?.data;
        console.error(
          `[Validacoes] ${label} (${path}) falhou: status=${status}`,
          data || err?.message,
        );
        return { ok: false, label, status, data };
      }
    },
    [getAuthHeader],
  );

  const notificarBadgeSidebar = useCallback((totalNovos) => {
    try {
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("validacoes:novos-atualizado", {
            detail: { total: totalNovos },
          }),
        );
      }
    } catch (_) {}
  }, []);

  const carregarNovos = useCallback(
    async ({ silencioso = false } = {}) => {
      if (!silencioso && novosRef.current.length === 0) {
        setLoadingNovos(true);
      }
      try {
        const [tca, tce, tcaEmp] = await Promise.all([
          safeGetFila("/tca/validacoes/novos", "TCA", true),
          safeGetFila("/tce/validacoes/novos", "TCE", true),
          safeGetFila("/tca-empregador/validacoes/novos", "TCA-Empregador", true),
        ]);

        const falhas = [tca, tce, tcaEmp].filter((r) => !r.ok);
        if (falhas.length) {
          console.error(
            "[carregarNovos] Endpoints com falha:",
            falhas.map((f) => `${f.label}=${f.status}`).join(", "),
          );
        }

        const ok = [tca, tce, tcaEmp].filter((r) => r.ok).map((r) => r.data);
        const listaNova = ordenarPorMaisRecente(deduplicar(ok.flat()));
        setNovos(listaNova);
        setPaginaNovos((p) => ajustarPagina(listaNova, p, tamanhoPagina));
        notificarBadgeSidebar(listaNova.length);

        if (
          typeof window !== "undefined" &&
          !(typeof process !== "undefined" && process.env?.NODE_ENV === "test")
        ) {
          window.__cieeValidacoesNovosListaCache = {
            ts: Date.now(),
            lista: listaNova,
          };
        }
      } catch (err) {
        console.error("Erro ao carregar novos:", err);
      } finally {
        setLoadingNovos(false);
      }
    },
    [safeGetFila, tamanhoPagina, notificarBadgeSidebar],
  );

  const carregarMinhas = useCallback(
    async ({ silencioso = false } = {}) => {
      if (!silencioso && minhasRef.current.length === 0) {
        setLoadingMinhas(true);
      }
      try {
        const [tca, tce, tcaEmp] = await Promise.all([
          safeGetFila("/tca/validacoes/minhas", "TCA", false),
          safeGetFila("/tce/validacoes/minhas", "TCE", false),
          safeGetFila("/tca-empregador/validacoes/minhas", "TCA-Empregador", false),
        ]);

        const falhas = [tca, tce, tcaEmp].filter((r) => !r.ok);
        if (falhas.length) {
          console.error(
            "[carregarMinhas] Endpoints com falha:",
            falhas.map((f) => `${f.label}=${f.status}`).join(", "),
          );
        }

        const ok = [tca, tce, tcaEmp].filter((r) => r.ok).map((r) => r.data);
        const listaNova = ordenarPorMaisRecente(deduplicar(ok.flat()));
        setMinhas(listaNova);
        setPaginaMinhas((p) => ajustarPagina(listaNova, p, tamanhoPagina));

        if (
          typeof window !== "undefined" &&
          !(typeof process !== "undefined" && process.env?.NODE_ENV === "test")
        ) {
          window.__cieeValidacoesMinhasListaCache = {
            ts: Date.now(),
            lista: listaNova,
          };
        }
      } catch (err) {
        console.error("Erro ao carregar minhas:", err);
      } finally {
        setLoadingMinhas(false);
      }
    },
    [safeGetFila, tamanhoPagina],
  );

  const atualizarFilasManualmente = useCallback(async () => {
    setAtualizandoManual(true);
    try {
      await Promise.all([
        carregarNovos({ silencioso: true }),
        carregarMinhas({ silencioso: true }),
      ]);
    } finally {
      setAtualizandoManual(false);
    }
  }, [carregarNovos, carregarMinhas]);

  useEffect(() => {
    let isMounted = true;
    if (!verificarPermissao()) {
      navigate("/dashboard", { replace: true });
      return;
    }
    const jaTemCache = novosRef.current.length > 0 || minhasRef.current.length > 0;
    carregarNovos({ silencioso: jaTemCache });
    carregarMinhas({ silencioso: jaTemCache });

    const interval = setInterval(() => {
      if (!isMounted) return;
      if (typeof document !== "undefined" && document.hidden) return;
      carregarNovos({ silencioso: true });
      carregarMinhas({ silencioso: true });
    }, 30000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const invalidarCachesAcompanhamento = useCallback(() => {
    try {
      invalidarCacheListaAcompanhamento();
      if (typeof window !== "undefined") {
        window.__cieeAcompanhamentosPreloaded = null;
      }
    } catch (_) {}
  }, []);

  const atribuir = async (item) => {
    const idDoc = resolverIdDoc(item);
    const chaveItem = `${tipoDoc(item)}-${idDoc}`;
    if (processandoChave === chaveItem) return;

    setProcessandoChave(chaveItem);
    try {
      await axios.post(
        `${API_BASE_URL}${endpointAtribuir(item)}`,
        {},
        { headers: getAuthHeader() },
      );
      toastSuccess(`Documento #${idDoc} atribuído para você!`);

      // Atualização otimista imediata (0ms) sem piscar a tabela
      const agoraIso = new Date().toISOString();
      const itemAtribuido = {
        ...item,
        id: idDoc,
        atribuido_em: item.atribuido_em || agoraIso,
      };
      setNovos((prev) => {
        const proximo = prev.filter(
          (i) => `${tipoDoc(i)}-${resolverIdDoc(i)}` !== chaveItem,
        );
        notificarBadgeSidebar(proximo.length);
        return proximo;
      });
      setMinhas((prev) => ordenarPorMaisRecente(deduplicar([itemAtribuido, ...prev])));

      invalidarCachesAcompanhamento();
      carregarNovos({ silencioso: true });
      carregarMinhas({ silencioso: true });
    } catch (err) {
      toastError(err.response?.data?.erro || "Erro ao atribuir documento.");
    } finally {
      setProcessandoChave(null);
    }
  };

  const liberar = async (item) => {
    const idDoc = resolverIdDoc(item);
    const chaveItem = `${tipoDoc(item)}-${idDoc}`;
    if (processandoChave === chaveItem) return;

    const ok = await confirm({
      title: "Liberar documento",
      message: `Liberar o documento #${idDoc} de volta para a fila?\nOutro operador poderá atribuí-lo.`,
      confirmText: "Sim, liberar",
      cancelText: "Cancelar",
      variant: "danger",
    });
    if (!ok) return;

    setProcessandoChave(chaveItem);
    try {
      await axios.post(
        `${API_BASE_URL}${endpointLiberar(item)}`,
        {},
        { headers: getAuthHeader() },
      );
      toastSuccess(`Documento #${idDoc} liberado de volta para a fila.`);

      // Atualização otimista imediata (0ms) sem piscar a tabela
      setMinhas((prev) =>
        prev.filter((i) => `${tipoDoc(i)}-${resolverIdDoc(i)}` !== chaveItem),
      );
      setNovos((prev) => {
        const proximo = ordenarPorMaisRecente(deduplicar([item, ...prev]));
        notificarBadgeSidebar(proximo.length);
        return proximo;
      });

      invalidarCachesAcompanhamento();
      carregarNovos({ silencioso: true });
      carregarMinhas({ silencioso: true });
    } catch (err) {
      toastError(err.response?.data?.erro || "Erro ao liberar documento.");
    } finally {
      setProcessandoChave(null);
    }
  };

  // Listas filtradas e contadores memoizados (evita recalcular 4x por renderização)
  const novosFiltrados = useMemo(
    () => filtrarLista(novos, termoBusca, filtroTipo),
    [novos, termoBusca, filtroTipo],
  );
  const minhasFiltrados = useMemo(
    () => filtrarLista(minhas, termoBusca, filtroTipo),
    [minhas, termoBusca, filtroTipo],
  );

  const contadoresAbaAtiva = useMemo(
    () => contarPorTipo(abaAtiva === "novos" ? novos : minhas),
    [abaAtiva, novos, minhas],
  );

  const temFiltroAtivo = Boolean(
    termoBusca?.trim() || (filtroTipo && filtroTipo !== "todos"),
  );

  const setPaginaAtiva = abaAtiva === "novos" ? setPaginaNovos : setPaginaMinhas;

  const renderBarraControles = (setPagina) => (
    <div className="val-controles-barra">
      <div className="val-busca-wrapper">
        <FaSearch className="val-busca-icone" />
        <input
          type="text"
          className="val-busca-input"
          placeholder="Buscar por Nº (#83), Estudante, Empresa ou CNPJ..."
          value={termoBusca}
          onChange={(e) => {
            setTermoBusca(e.target.value);
            setDiagnostico(null);
            setPagina(1);
          }}
        />
        {termoBusca && (
          <button
            type="button"
            className="val-busca-limpar"
            onClick={() => {
              setTermoBusca("");
              setDiagnostico(null);
              setPagina(1);
            }}
            title="Limpar busca"
          >
            <FaTimes />
          </button>
        )}
      </div>

      <div className="val-filtros-grupo">
        <label className="val-filtro-tipo">
          Tipo:{" "}
          <select
            value={filtroTipo}
            onChange={(e) => {
              setFiltroTipo(e.target.value);
              setDiagnostico(null);
              setPagina(1);
            }}
          >
            <option value="todos">
              Todos os tipos ({contadoresAbaAtiva.todos})
            </option>
            <option value="TCA-Empregador">
              TCA Empregador ({contadoresAbaAtiva.tcaEmpregador})
            </option>
            <option value="TCA">TCA ({contadoresAbaAtiva.tca})</option>
            <option value="TCE">TCE ({contadoresAbaAtiva.tce})</option>
          </select>
        </label>

        <button
          type="button"
          className="val-btn val-btn-secondary"
          onClick={atualizarFilasManualmente}
          disabled={atualizandoManual}
          title="Sincronizar fila agora"
          style={{ padding: "6px 12px", fontSize: "0.82rem" }}
        >
          <FaSyncAlt
            style={
              atualizandoManual
                ? { animation: "spin 0.9s linear infinite" }
                : undefined
            }
          />
          {atualizandoManual ? "Sincronizando..." : "Atualizar"}
        </button>
      </div>
    </div>
  );

  const renderTabela = ({
    listaOriginal,
    listaFiltrada,
    outraListaFiltrada,
    loading,
    acao,
    pagina,
    setPagina,
    outraAbaId,
    nomeOutraAba,
  }) => {
    const qLimpo = termoBusca?.trim().replace(/^#/, "");
    const idBuscado = /^\d+$/.test(qLimpo) ? qLimpo : null;

    if (loading && listaOriginal.length === 0) {
      return (
        <>
          {renderBarraControles(setPagina)}
          <p className="val-loading">Carregando documentos da fila...</p>
        </>
      );
    }

    if (listaOriginal.length === 0 && !temFiltroAtivo) {
      return (
        <>
          {renderBarraControles(setPagina)}
          <p className="val-empty">
            {acao === "novos"
              ? "Nenhum documento aguardando atribuição."
              : "Você não tem documentos atribuídos."}
          </p>
        </>
      );
    }

    if (listaFiltrada.length === 0) {
      return (
        <>
          {renderBarraControles(setPagina)}

          {outraListaFiltrada.length > 0 && (
            <div className="val-alerta-outra-aba">
              <div className="val-alerta-conteudo">
                <FaInfoCircle className="val-alerta-icone" />
                <div>
                  <strong>Documento encontrado na outra aba:</strong> Encontrado(s){" "}
                  <strong>{outraListaFiltrada.length}</strong> documento(s) com o critério &quot;{termoBusca}&quot; na aba{" "}
                  <strong>&quot;{nomeOutraAba}&quot;</strong>.
                </div>
              </div>
              <button
                type="button"
                className="val-alerta-btn"
                onClick={() => {
                  setAbaAtiva(outraAbaId);
                  setDiagnostico(null);
                }}
              >
                Ver em {nomeOutraAba} ›
              </button>
            </div>
          )}

          {outraListaFiltrada.length === 0 && idBuscado && (
            <div className="val-card-diagnostico">
              <div className="val-diagnostico-header">
                <FaSearchPlus className="val-diagnostico-icone" />
                <div>
                  <h4>Não encontrou o contrato #{idBuscado} na fila de validação?</h4>
                  <p>
                    O documento pode estar aguardando preenchimento do estudante, já atribuído a outro operador, ou ter outro status.
                  </p>
                </div>
              </div>

              {!diagnostico && (
                <button
                  type="button"
                  className="val-btn-diagnostico"
                  onClick={() => consultarDocumentoPorId(idBuscado)}
                  disabled={loadingDiagnostico}
                >
                  {loadingDiagnostico
                    ? "Consultando banco de dados..."
                    : `Verificar status do contrato #${idBuscado} no sistema`}
                </button>
              )}

              {diagnostico && diagnostico.encontrado && (
                <div className="val-diagnostico-resultado">
                  <div className="val-diag-badge-sucesso">
                    Documento localizado no banco de dados!
                  </div>
                  {(diagnostico.encontrados || [diagnostico]).map((docDiag) => (
                    <div
                      key={`${docDiag.tipo}-${docDiag.id}`}
                      style={{
                        paddingBottom: 10,
                        marginBottom: 10,
                        borderBottom:
                          (diagnostico.encontrados?.length || 1) > 1
                            ? "1px solid #e2e8f0"
                            : "none",
                      }}
                    >
                      <div className="val-diag-grid">
                        <div>
                          <strong>Tipo:</strong> {docDiag.tipo}
                        </div>
                        <div>
                          <strong>Nº Documento:</strong> #{docDiag.id}
                        </div>
                        <div>
                          <strong>Empresa:</strong> {docDiag.empresaNome} ({docDiag.cnpjVal})
                        </div>
                        <div>
                          <strong>Estudante:</strong> {docDiag.estudanteNome}
                        </div>
                        <div>
                          <strong>Status no Sistema:</strong>{" "}
                          <span className="val-status-destaque">{docDiag.status}</span>
                        </div>
                        {docDiag.validadorId && (
                          <div className="val-diag-validador">
                            ⚠️ Documento já está atribuído ao validador ID {docDiag.validadorId}.
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        className="val-btn-abrir-doc"
                        onClick={() => abrirDocumento(docDiag.urlDoc, docDiag.origem)}
                      >
                        <FaExternalLinkAlt /> Abrir {docDiag.tipo} #{docDiag.id} diretamente
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {diagnostico && !diagnostico.encontrado && (
                <div className="val-diag-nao-encontrado">
                  Nenhum contrato com ID #{diagnostico.id} foi localizado no banco (TCA Empregador, TCA ou TCE).
                </div>
              )}
            </div>
          )}

          <p className="val-empty">
            Nenhum documento encontrado na aba atual para a busca &quot;{termoBusca}&quot;
            {filtroTipo !== "todos" ? ` com tipo ${filtroTipo}` : ""}.
          </p>
        </>
      );
    }

    const { itens, totalPaginas, total, paginaSegura } = recortarPagina(
      listaFiltrada,
      pagina,
      tamanhoPagina,
    );
    const inicio = (paginaSegura - 1) * tamanhoPagina + 1;
    const fim = inicio + itens.length - 1;

    const paginasVisiveis = (() => {
      const arr = [];
      const max = totalPaginas;
      const p = paginaSegura;
      if (max <= 7) {
        for (let i = 1; i <= max; i++) arr.push(i);
      } else {
        arr.push(1);
        if (p > 4) arr.push("...");
        const inicioJanela = Math.max(2, p - 1);
        const fimJanela = Math.min(max - 1, p + 1);
        for (let i = inicioJanela; i <= fimJanela; i++) arr.push(i);
        if (p < max - 3) arr.push("...");
        arr.push(max);
      }
      return arr;
    })();

    return (
      <>
        {renderBarraControles(setPagina)}

        {temFiltroAtivo && outraListaFiltrada.length > 0 && (
          <div className="val-dica-outra-aba">
            <FaInfoCircle size={14} /> Dica: há também{" "}
            <strong>{outraListaFiltrada.length}</strong> documento(s) com este termo na aba{" "}
            <button
              type="button"
              className="val-link-trocar-aba"
              onClick={() => {
                setAbaAtiva(outraAbaId);
                setDiagnostico(null);
              }}
            >
              &quot;{nomeOutraAba}&quot;
            </button>
            .
          </div>
        )}

        <div className="val-table-meta">
          <span className="val-table-info">
            Mostrando <strong>{inicio}</strong>–<strong>{fim}</strong> de{" "}
            <strong>{total}</strong> documentos{" "}
            {listaFiltrada.length !== listaOriginal.length &&
              `(filtrados de ${listaOriginal.length})`}
          </span>
          <label className="val-pagesize">
            Por página:{" "}
            <select
              value={tamanhoPagina}
              onChange={(e) => {
                const novo = Number(e.target.value) || 10;
                setTamanhoPagina(novo);
                setPagina(1);
              }}
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        </div>

        <table className="val-table">
          <thead>
            <tr>
              <th>Nº Documento</th>
              <th>CNPJ/ID</th>
              <th>Empresa</th>
              <th>Estudante</th>
              <th>Tipo</th>
              <th>Início Vigência</th>
              <th>Encaminhado em</th>
              <th>{acao === "novos" ? "Entrada na fila" : "Atribuído em"}</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {itens.map((item) => {
              const idDoc = resolverIdDoc(item);
              const key = `${tipoDoc(item)}-${idDoc}`;
              const cnpjOuDoc = resolverCnpjDoc(item);
              const nomeEmpresa = resolverNomeEmpresaDoc(item);
              const nomeEstudante = resolverNomeEstudanteDoc(item);
              const emProcessamento = processandoChave === key;

              return (
                <tr key={key}>
                  <td>
                    <button
                      type="button"
                      className="val-doc-id-btn"
                      onClick={() => abrirDocumento(item)}
                      title={`Abrir ${tipoDoc(item)} #${idDoc}`}
                    >
                      <strong className="val-id-num">#{idDoc}</strong>
                    </button>
                    {(item.estudante_id || item.empresa_id) && (
                      <div
                        className="val-doc-ref"
                        title="Referência Estudante - Empresa"
                      >
                        {item.estudante_id || "N/A"}-{item.empresa_id || "N/A"}
                      </div>
                    )}
                  </td>
                  <td>{formatarCnpjCpf(cnpjOuDoc)}</td>
                  <td>{nomeEmpresa}</td>
                  <td>{nomeEstudante}</td>
                  <td>
                    <span className="val-tipo-badge">{tipoDoc(item)}</span>
                  </td>
                  <td>
                    {item.data_inicio_contrato
                      ? formatarDataApenas(
                          toDateInputValue(item.data_inicio_contrato),
                        )
                      : "-"}
                  </td>
                  <td>{formatarDataHoraCurta(dataConclusaoItem(item))}</td>
                  <td>
                    {acao === "novos"
                      ? formatarDataHoraBR(item.created_at)
                      : formatarDataHoraBR(item.atribuido_em)}
                  </td>
                  <td>
                    <span
                      className={`val-status-badge val-status-${(
                        item.status || "PENDENTE"
                      ).toLowerCase()}`}
                    >
                      {item.status || "PENDENTE"}
                    </span>
                  </td>
                  <td className="val-acoes">
                    {acao === "novos" ? (
                      <>
                        <button
                          className="val-btn-icon val-btn-attr"
                          onClick={() => atribuir(item)}
                          disabled={emProcessamento}
                          title="Atribuir para mim"
                        >
                          <FaHandPointer />
                        </button>
                        <button
                          className="val-btn-icon val-btn-view"
                          onClick={() => abrirDocumento(item)}
                          title="Visualizar documento"
                        >
                          <FaEye />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="val-btn-icon val-btn-view"
                          onClick={() => abrirDocumento(item)}
                          title="Abrir documento"
                        >
                          <FaEye />
                        </button>
                        <button
                          className="val-btn-icon val-btn-lib"
                          onClick={() => liberar(item)}
                          disabled={emProcessamento}
                          title="Liberar"
                        >
                          <FaHandshake />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <nav className="val-paginacao" aria-label="Paginação">
          <button
            className="val-page-btn"
            onClick={() => setPagina(paginaSegura - 1)}
            disabled={paginaSegura <= 1}
            title="Página anterior"
          >
            ‹ Anterior
          </button>
          {paginasVisiveis.map((p, i) =>
            p === "..." ? (
              <span key={`el-${i}`} className="val-page-ellipsis">
                …
              </span>
            ) : (
              <button
                key={p}
                className={`val-page-btn ${p === paginaSegura ? "val-page-ativa" : ""}`}
                onClick={() => setPagina(p)}
                aria-current={p === paginaSegura ? "page" : undefined}
              >
                {p}
              </button>
            ),
          )}
          <button
            className="val-page-btn"
            onClick={() => setPagina(paginaSegura + 1)}
            disabled={paginaSegura >= totalPaginas}
            title="Próxima página"
          >
            Próxima ›
          </button>
        </nav>
      </>
    );
  };

  if (!verificarPermissao()) return null;

  return (
    <div className="val-container">
      <header className="val-header">
        <div className="val-header-titulo">
          <FaClipboardCheck size={26} />
          <h1>Validações — Fila de Documentos</h1>
        </div>
        <button
          className="val-btn val-btn-secondary"
          onClick={() => navigate("/dashboard")}
        >
          <FaArrowLeft /> Voltar
        </button>
      </header>

      <div className="val-tabs">
        <button
          className={`val-tab ${abaAtiva === "novos" ? "val-tab-ativa" : ""}`}
          onClick={() => {
            setAbaAtiva("novos");
            setDiagnostico(null);
          }}
        >
          Novos
          <span className="val-tab-badge">
            {temFiltroAtivo ? `${novosFiltrados.length}` : novos.length}
          </span>
        </button>
        <button
          className={`val-tab ${abaAtiva === "minhas" ? "val-tab-ativa" : ""}`}
          onClick={() => {
            setAbaAtiva("minhas");
            setDiagnostico(null);
          }}
        >
          Minhas Validações
          <span className="val-tab-badge val-tab-badge-minhas">
            {temFiltroAtivo ? `${minhasFiltrados.length}` : minhas.length}
          </span>
        </button>
      </div>

      {abaAtiva === "novos" && (
        <section className="val-section">
          <h2>Documentos Aguardando Atribuição</h2>
          {renderTabela({
            listaOriginal: novos,
            listaFiltrada: novosFiltrados,
            outraListaFiltrada: minhasFiltrados,
            loading: loadingNovos,
            acao: "novos",
            pagina: paginaNovos,
            setPagina: setPaginaAtiva,
            outraAbaId: "minhas",
            nomeOutraAba: "Minhas Validações",
          })}
        </section>
      )}

      {abaAtiva === "minhas" && (
        <section className="val-section">
          <h2>Meus Documentos em Validação</h2>
          {renderTabela({
            listaOriginal: minhas,
            listaFiltrada: minhasFiltrados,
            outraListaFiltrada: novosFiltrados,
            loading: loadingMinhas,
            acao: "minhas",
            pagina: paginaMinhas,
            setPagina: setPaginaAtiva,
            outraAbaId: "novos",
            nomeOutraAba: "Novos",
          })}
        </section>
      )}

      <ToastContainer toasts={toasts} onRemove={removeToast} />
      {confirmDialog}
    </div>
  );
};

export default Validacoes;
